import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { walletAPI } from '../../services/api';

export default function UpdatePayment() {
  const { user } = useAuth();
  const [form, setForm] = useState({
    amount: '', 
    transfer_at: '', 
    utr_number: '', 
    description: '',
    requester_role: user?.role || 'user'
  });
  const [slip, setSlip] = useState(null);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  const onChange = e => {
    const { name, value } = e.target;
    setForm(prev => ({...prev, [name]: value}));
    // Clear field error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({...prev, [name]: ''}));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); 
    setMsg('');
    setError('');
    setFieldErrors({});
    
    // Client-side validation
    const newFieldErrors = {};
    
    // Validate amount
    if (!form.amount || form.amount <= 0) {
      newFieldErrors.amount = 'Amount must be greater than 0';
    } else if (form.amount > 100000000) {
      newFieldErrors.amount = 'Amount cannot exceed ₹100,000,000';
    }
    
    // Validate transfer date
    if (!form.transfer_at) {
      newFieldErrors.transfer_at = 'Transfer date is required';
    } else {
      const transferDate = new Date(form.transfer_at);
      const now = new Date();
      if (transferDate > now) {
        newFieldErrors.transfer_at = 'Transfer date cannot be in the future';
      }
    }
    
    // Validate UTR number
    if (!form.utr_number || form.utr_number.trim().length === 0) {
      newFieldErrors.utr_number = 'UTR number is required';
    } else if (form.utr_number.length > 64) {
      newFieldErrors.utr_number = 'UTR number cannot exceed 64 characters';
    }
    
    // Validate description
    if (form.description && form.description.length > 500) {
      newFieldErrors.description = 'Description cannot exceed 500 characters';
    }
    
    // Validate file size
    if (slip && slip.size > 2 * 1024 * 1024) {
      newFieldErrors.slip = 'File size must be less than 2MB';
    }
    
    if (Object.keys(newFieldErrors).length > 0) {
      setFieldErrors(newFieldErrors);
      setLoading(false);
      return;
    }
    
    const fd = new FormData();
    Object.entries(form).forEach(([k,v]) => {
      if (k === 'transfer_at' && v) {
        // Convert datetime-local format to backend expected format
        const date = new Date(v);
        fd.append(k, date.toISOString().slice(0, 19).replace('T', ' '));
      } else {
        fd.append(k, v);
      }
    });
    
    // Only append slip if it's a valid file
    if (slip && slip instanceof File) {
      fd.append('slip', slip);
    }

    try {
      const res = await walletAPI.submitPaymentRequest(fd);
      setMsg('Payment request submitted successfully! It will be reviewed by admin.');
      setForm({
        amount: '', 
        transfer_at: '', 
        utr_number: '', 
        description: '',
        requester_role: user?.role || 'user'
      });
      setSlip(null);
    } catch (err) {
      console.error('Payment request error:', err);
      
      if (err.response?.status === 422) {
        // Handle validation errors
        const errors = err.response?.data?.errors;
        if (errors) {
          // Set field-specific errors
          const serverFieldErrors = {};
          Object.entries(errors).forEach(([field, messages]) => {
            serverFieldErrors[field] = Array.isArray(messages) ? messages.join(', ') : messages;
          });
          setFieldErrors(serverFieldErrors);
          
          // Also show general error message
          const errorMessages = Object.entries(errors).map(([field, messages]) => 
            `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`
          );
          setError(`Validation failed: ${errorMessages.join('; ')}`);
        } else {
          setError(err.response?.data?.message || 'Validation failed. Please check your input.');
        }
      } else {
        setError(err.response?.data?.message || err.message || 'Failed to submit request');
      }
    } finally { 
      setLoading(false); 
    }
  };

  return (
    <div className="max-w-xl mx-auto p-4">
      <h2 className="text-xl font-semibold mb-3">Update / Approve Payment</h2>
      <form onSubmit={submit} className="space-y-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Amount (₹)</label>
          <input 
            name="amount" 
            type="number" 
            step="0.01" 
            placeholder="Enter amount" 
            value={form.amount} 
            onChange={onChange} 
            className={`border p-2 w-full rounded ${fieldErrors.amount ? 'border-red-500' : ''}`}
            required 
          />
          {fieldErrors.amount && <p className="text-red-500 text-xs mt-1">{fieldErrors.amount}</p>}
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Transfer Date & Time</label>
          <input 
            name="transfer_at" 
            type="datetime-local" 
            value={form.transfer_at} 
            onChange={onChange} 
            className={`border p-2 w-full rounded ${fieldErrors.transfer_at ? 'border-red-500' : ''}`}
            required 
          />
          {fieldErrors.transfer_at && <p className="text-red-500 text-xs mt-1">{fieldErrors.transfer_at}</p>}
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">UTR Number</label>
          <input 
            name="utr_number" 
            placeholder="Enter UTR number" 
            value={form.utr_number} 
            onChange={onChange} 
            className={`border p-2 w-full rounded ${fieldErrors.utr_number ? 'border-red-500' : ''}`}
            required 
          />
          {fieldErrors.utr_number && <p className="text-red-500 text-xs mt-1">{fieldErrors.utr_number}</p>}
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Description (Optional)</label>
          <textarea 
            name="description" 
            placeholder="Enter description" 
            value={form.description} 
            onChange={onChange} 
            className={`border p-2 w-full rounded ${fieldErrors.description ? 'border-red-500' : ''}`}
            rows="3"
          />
          {fieldErrors.description && <p className="text-red-500 text-xs mt-1">{fieldErrors.description}</p>}
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Payment Slip</label>
          <input 
            type="file" 
            accept=".jpg,.jpeg,.png,.pdf" 
            onChange={e=>{
              setSlip(e.target.files?.[0] || null);
              // Clear file error when user selects a new file
              if (fieldErrors.slip) {
                setFieldErrors(prev => ({...prev, slip: ''}));
              }
            }} 
            className={`border p-2 w-full rounded ${fieldErrors.slip ? 'border-red-500' : ''}`}
          />
          <p className="text-xs text-gray-500 mt-1">Upload JPG, PNG, or PDF (max 2MB)</p>
          {fieldErrors.slip && <p className="text-red-500 text-xs mt-1">{fieldErrors.slip}</p>}
        </div>
        
        <button 
          disabled={loading} 
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50 w-full"
        >
          {loading ? 'Submitting...' : 'Submit Request'}
        </button>
      </form>
      
      {msg && <p className="mt-3 text-sm text-green-600 bg-green-100 p-2 rounded">{msg}</p>}
      {error && <p className="mt-3 text-sm text-red-600 bg-red-100 p-2 rounded">{error}</p>}
    </div>
  );
}
